// Pure vision-resolution logic — no store, no React. Mirrors the shape of
// cycle-state.ts: the resolution rule is the whole point of the rebuild, so it
// stays small, total, and unit-testable rather than buried in the store.
import { cycleOrder, cycleLabel } from '@/modules/cycles/cycle-state';
import type { Vision } from '@/modules/vision/types';
import type { VisionData } from '@/modules/vision/template-types';

/**
 * Format the label for a vision's effective cycle span.
 * - Single quarter: "Q1 2026"
 * - Multi-quarter, same year: "Q1–Q3 2026"
 * - Full year (no quarter): "2026"
 * - Multi-year: "Q3 2026–Q1 2027"
 */
export function spanRangeLabel(
  origin: { year: number; quarter: number | null },
  cyclesInSpan: { year: number; quarter: number | null }[],
): string {
  const first = cyclesInSpan[0] ?? origin;
  const last = cyclesInSpan[cyclesInSpan.length - 1] ?? origin;
  if (cycleOrder(first.year, first.quarter) === cycleOrder(last.year, last.quarter)) {
    return cycleLabel(first.year, first.quarter);
  }
  if (first.year === last.year && first.quarter != null && last.quarter != null) {
    return `Q${first.quarter}–Q${last.quarter} ${first.year}`;
  }
  return `${cycleLabel(first.year, first.quarter)}–${cycleLabel(last.year, last.quarter)}`;
}

/**
 * A per-team, per-cycle vision decision, flattened for resolution: which team
 * chose which vision at which cycle. `year`/`quarter` are denormalised from the
 * cycle so the resolver never needs a join.
 *
 * `visionId: null` is an explicit "inherit from the parent team, from here on"
 * decision — distinct from there being no row at all. See TEAMS.md § 5.2.
 */
export interface VisionDecision {
  cycleId: string;
  teamId: string;
  visionId: string | null;
  year: number;
  quarter: number | null;
}

/**
 * Flattens stored vision decisions for resolution, denormalising each cycle's
 * period so nothing downstream needs a join. Decisions whose cycle has since
 * been dropped are skipped rather than resolved against a missing period.
 */
export function visionDecisionsOf(
  cycles: { id: string; year: number; quarter: number | null }[],
  cycleVisions: { cycleId: string; teamId: string; visionId: string | null }[],
): VisionDecision[] {
  const byId = new Map(cycles.map((c) => [c.id, c]));
  const out: VisionDecision[] = [];
  for (const cv of cycleVisions) {
    const cycle = byId.get(cv.cycleId);
    if (!cycle) continue;
    out.push({
      cycleId: cv.cycleId,
      teamId: cv.teamId,
      visionId: cv.visionId,
      year: cycle.year,
      quarter: cycle.quarter,
    });
  }
  return out;
}

/** The nearest decision at or before a cycle, for one team. */
function nearestDecision(
  decisions: VisionDecision[],
  teamId: string,
  target: number,
): VisionDecision | null {
  return decisions
    .filter((p) => p.teamId === teamId && cycleOrder(p.year, p.quarter) <= target)
    .sort((a, b) => cycleOrder(b.year, b.quarter) - cycleOrder(a.year, a.quarter))[0] ?? null;
}

/**
 * The vision in force for a cycle, resolved in two dimensions: **back through
 * cycles, then up through ancestor teams.**
 *
 * Backward through cycles, never forward — a forward scan would show a vision
 * not yet in force at that cycle. Up through `teamChain` (nearest-first,
 * `[ownTeam, ...ancestors]`) when this team has no point of its own, or its
 * nearest point is an inherit tombstone. Most teams therefore never create a
 * vision at all; they inherit the company's.
 *
 * With cyclical planning off, cycles carry no meaningful ordering. The
 * workspace has one governing vision at a time — the most recently updated
 * one, not just the first in array order (fetch order isn't guaranteed, and
 * an acyclical workspace can still carry stray extra visions left over from
 * a period cyclical planning was on: toggling it off never deletes them, so
 * picking deterministically is what keeps the single-vision read stable
 * across reloads instead of flapping between them).
 */
export function resolveVision(
  points: VisionDecision[],
  visions: Vision[],
  year: number | null,
  quarter: number | null,
  cyclicalPlanning: boolean,
  teamChain: string[],
): Vision | null {
  if (!cyclicalPlanning || year == null) {
    if (visions.length === 0) return null;
    // THE CHAIN APPLIES HERE TOO (#150). This branch used to take the most
    // recently updated vision in the workspace, which was right while every
    // document was homed at the root and becomes wrong the moment they are not:
    // an acyclical Marketing would inherit whichever team happened to save
    // last, including one it cannot read.
    //
    // Same rule as the cyclical branch, minus the cycle: nearest team in the
    // chain that owns any document wins, and within it the most recent. No
    // tombstones, because there are no revision points to carry one — a team
    // with no vision of its own simply has none, and the walk continues.
    const homeOf = (v: Vision) => v.teamId ?? teamChain[teamChain.length - 1];
    for (const teamId of teamChain) {
      const here = visions.filter((v) => homeOf(v) === teamId);
      if (here.length) return here.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
    }
    // Nothing anywhere in the chain. Falling back to the workspace's newest
    // preserves the pre-teams answer for a tenant whose documents predate
    // `team_id` being written, rather than blanking a bulletin that worked
    // yesterday.
    return [...visions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  }
  const target = cycleOrder(year, quarter);
  for (const teamId of teamChain) {
    const nearest = nearestDecision(points, teamId, target);
    // No decision on this team, or an explicit inherit tombstone: keep walking up.
    if (!nearest || nearest.visionId == null) continue;
    return visions.find((v) => v.id === nearest.visionId) ?? null;
  }
  return null;
}

export interface VisionSpan {
  origin: { year: number; quarter: number | null } | null;
  /** The next decision strictly after the origin on the same team, if any — the span's exclusive end. */
  nextOrigin: { year: number; quarter: number | null } | null;
}

/**
 * A vision's span — its origin cycle through (exclusive of) the next decision
 * *on the same team*. A decision on a sibling team doesn't end it.
 */
export function visionSpan(points: VisionDecision[], visionId: string): VisionSpan {
  const origin = points.find((p) => p.visionId === visionId) ?? null;
  if (!origin) return { origin: null, nextOrigin: null };
  const order = cycleOrder(origin.year, origin.quarter);
  const next = points
    .filter((p) => p.teamId === origin.teamId && p.visionId !== visionId && cycleOrder(p.year, p.quarter) > order)
    .sort((a, b) => cycleOrder(a.year, a.quarter) - cycleOrder(b.year, b.quarter))[0] ?? null;
  return {
    origin: { year: origin.year, quarter: origin.quarter },
    nextOrigin: next ? { year: next.year, quarter: next.quarter } : null,
  };
}

export interface VisionDeleteImpact {
  /** Deleting this vision would leave some cycle resolving to nothing, or it's the only vision left. */
  blocked: boolean;
  reason: 'only-vision' | 'would-orphan-cycles' | null;
  /** Cycles currently resolving to this vision — what a non-blocked delete changes the resolution of. */
  affected: { year: number; quarter: number | null }[];
  /** The vision that affected cycles will resolve to after the delete. Null when blocked or unattached. */
  replacementVisionId: string | null;
}

/**
 * What deleting a vision would do. Blocked when the root team would be left
 * resolving to nothing at some cycle, or when this vision is a decision with no
 * earlier one on its team to fall back to. Otherwise lists the cycles in its
 * span, which move to whatever the previous decision (or nothing, if unattached)
 * covers instead.
 *
 * Scoped to the vision's own team throughout: another team's decisions neither
 * end its span nor serve as its fallback.
 *
 * `periods` is the full cycle list, not just the ones carrying a decision — the
 * affected span includes the quarters in between, which have no decision of
 * their own precisely because they carry forward this vision.
 *
 * `rootChain` is the ancestor chain for the root team. When provided, the
 * `only-vision` block fires when deleting would leave root resolving to nothing
 * at any period, rather than on a simple count.
 */
export function visionDeleteImpact(
  points: VisionDecision[],
  periods: { year: number; quarter: number | null }[],
  visions: Vision[],
  visionId: string,
  rootChain?: string[],
): VisionDeleteImpact {
  const remainingPoints = points.filter((p) => p.visionId !== visionId);
  if (rootChain && rootChain.length > 0) {
    const hasRemainingRootDecision = remainingPoints.some(
      (p) => rootChain.includes(p.teamId ?? '') && p.visionId != null,
    );
    if (!hasRemainingRootDecision) return { blocked: true, reason: 'only-vision', affected: [], replacementVisionId: null };
  } else if (visions.length <= 1) {
    return { blocked: true, reason: 'only-vision', affected: [], replacementVisionId: null };
  }
  const origin = points.find((p) => p.visionId === visionId) ?? null;
  if (!origin) return { blocked: false, reason: null, affected: [], replacementVisionId: null };
  const { nextOrigin } = visionSpan(points, visionId);
  const order = cycleOrder(origin.year, origin.quarter);
  const affected = periods
    .filter((p) => {
      const o = cycleOrder(p.year, p.quarter);
      return o >= order && (nextOrigin == null || o < cycleOrder(nextOrigin.year, nextOrigin.quarter));
    })
    .sort((a, b) => cycleOrder(a.year, a.quarter) - cycleOrder(b.year, b.quarter))
    .map((p) => ({ year: p.year, quarter: p.quarter }));
  // The most recent prior decision on the same team — what affected periods fall back to.
  const priorDecision = points
    .filter((p) => p.teamId === origin.teamId && p.visionId != null && p.visionId !== visionId && cycleOrder(p.year, p.quarter) < order)
    .sort((a, b) => cycleOrder(b.year, b.quarter) - cycleOrder(a.year, a.quarter))[0];
  const replacementVisionId = priorDecision?.visionId ?? null;
  const hasEarlier = replacementVisionId != null;
  return { blocked: !hasEarlier, reason: hasEarlier ? null : 'would-orphan-cycles', affected, replacementVisionId };
}

/**
 * "The open vision" — the one vision that can be ratified. Not "not
 * superseded" (a future planning-quarter draft doesn't retire the vision
 * still governing the active quarter until that quarter actually opens),
 * and not "origin cycle is open" (a vision created in a now-closed quarter
 * that nothing has since revised is still the open vision). It's simply
 * whichever vision resolves for the workspace's actual active cycle.
 */
export function isOpenVision(
  points: VisionDecision[],
  visions: Vision[],
  visionId: string,
  activeYear: number | null,
  activeQuarter: number | null,
  cyclicalPlanning: boolean,
  teamChain: string[],
): boolean {
  const open = resolveVision(points, visions, activeYear, activeQuarter, cyclicalPlanning, teamChain);
  return open?.id === visionId;
}

/**
 * The teamId whose vision would be inherited if `teamId` chose to inherit for
 * this cycle. Walks the ancestor chain (skipping `teamId` itself) and returns
 * the first team that has a real decision — or null if the chain has nothing.
 */
export function inheritSourceTeamId(
  decisions: VisionDecision[],
  year: number,
  quarter: number | null,
  ancestorChain: string[],
  teamId: string,
): string | null {
  const target = cycleOrder(year, quarter);
  for (const sid of ancestorChain) {
    if (sid === teamId) continue;
    const nearest = nearestDecision(decisions, sid, target);
    if (nearest && nearest.visionId != null) return sid;
  }
  return null;
}

/** "<source name> (revised for Q3)" / "(revised for 2026)" for an annual target. */
export function reviseAutoName(sourceName: string, year: number, quarter: number | null): string {
  return `${sourceName} (revised for ${quarter ? `Q${quarter}` : year})`;
}

/** Clears `one-year-*` keys — what the yearly branch resets so the new year opens blank. */
export function clearOneYearKeys(data: VisionData): VisionData {
  const next = { ...data };
  for (const key of Object.keys(next)) {
    if (key.startsWith('one-year-')) delete next[key];
  }
  return next;
}
